import type { TableModel } from '../model/table';
import { choiceEligible, type ChoiceView } from '../model/choices';
import { CatalogMetadata } from './CatalogMetadata';
import { PublicProgress } from './PublicProgress';
import { PieceFace } from '../bricks/Piece';

interface Props {
  table: TableModel;
  objectId: string | null;
  choices: ChoiceView[];
  onClose: () => void;
  onChoose: (choiceId: string) => void;
  label?: string;
}

export function Inspector({ table, objectId, choices, onClose, onChoose, label = 'Details' }: Props) {
  const object = objectId ? table.objects.get(objectId) : undefined;
  if (!object) {
    return (
      <section className="panel inspector" aria-label="Details">
        <h2 className="panel-title">{label}</h2>
        <p className="muted">Select a card or piece to see its details and the moves that use it.</p>
      </section>
    );
  }
  const related = choices.filter(c => choiceEligible(table, c) && c.subjects.includes(object.id));
  const zone = table.zones.find(z => z.id === object.zoneId);
  return (
    <section className="panel inspector" aria-label={`Details: ${object.title}`}>
      <div className="panel-heading">
        <h2 className="inspector-title">{object.title}</h2>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close details">×</button>
      </div>
      {object.kind === 'card' && <div className="inspector-face"><PieceFace object={object} zoneKind={zone?.kind ?? null} width={136} /></div>}
      {object.label !== object.title && <p className="inspector-label">{object.label}</p>}
      {object.catalog?.text && <p className="inspector-text">{object.catalog.text}</p>}
      <CatalogMetadata object={object} />
      <PublicProgress object={object} table={table} />
      <dl className="detail-list">
        {zone && <div><dt>Location</dt><dd>{zone.label}</dd></div>}
        {object.points !== null && (object.kind !== 'card' || object.points !== '0') && <div><dt>Points</dt><dd>{object.points}</dd></div>}
        {object.costs.length > 0 && <div><dt>Cost</dt><dd>{object.costs.map(c => `${c.value} ${c.key}`).join(', ')}</dd></div>}
        {object.details.map(d => <div key={d.key}><dt>{d.key}</dt><dd>{d.value}</dd></div>)}
      </dl>
      {related.length > 0 && (
        <div className="inspector-moves">
          <h3 className="subhead">Moves with this</h3>
          <ul className="choice-list">
            {related.map(c => (
              <li key={c.id}><button type="button" className="choice-button" onClick={() => onChoose(c.id)}>{c.label}</button></li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
